import { isProduction } from '@quadminds/shared/utils';
import { AnimatePresence, motion } from 'framer-motion';
import moment from 'moment';
import React, { useMemo } from 'react';
import { MINDY_VERSION } from '../../../../config/index';
import { CHAT_MODE } from '../../../../enums/chatMode.enum';
import { SENDER } from '../../../../enums/sender.enum';
import { SOURCE } from '../../../../enums/source.enum';
import useChat from '../../../../hooks/useChat';
import { useIsKnowledge } from '../../../../hooks/useIsKnowledge';
import AnimatedDots from '../../../common/AnimatedDots/AnimatedDots';
import { Cards } from '../Cards/Cards';
import { Input } from '../Input/Input';
import { Response } from '../Messages/Response/Response';
import { UserMessage } from '../Messages/User/User';
import { MobileAppBanner } from '../MobileAppBanner/MobileAppBanner';
import { ThinkingLoader } from '../ThinkingLoader';

export function Chat() {
    const {
        chatContainerRef,
        inputContainerRef,
        firebaseIdToken,
        userInfo,
        conversationId,
        ref,
        inputRef,
        t,
        chatInputValue,
        setChatInputValue,
        files,
        setFiles,
        messages,
        showDisclaimer,
        suggestedQuestionsQuantity,
        customBackgroundImage,
        suggestedQuestions,
        defaultSuggestedQuestionTitles,
        handleSendLogisticsCard,
        isActive,
        handleSendMessage,
        isLoading,
        mode,
        showKnowledgeEmbeddingSelector,
        knowledgeEmbeddingDestination,
        setKnowledgeEmbeddingDestination,
        showRestScrollButton,
        buttonTop,
        isDifferentAgentVersion,
        agentVersion,
        isStreaming,
        activeKnowledgeRequestId,
        bottomRef,
        messagesListRef,
        progressiveThinkingMessage,
        isModeLocked,
        scrollToBottomAndUnlock,
        handleCancelStream,
    } = useChat();

    const { isKnowledge, isLoading: isKnowledgeLoading } = useIsKnowledge();

    // Compute all payloads once per messages change — avoids O(N²) slice+map on every render
    const messagePayloads = useMemo(
        () =>
            messages.map((_, index) => ({
                user_email: userInfo?.email || '',
                feedback_date: moment().format('DD-MM-YYYY HH:mm:ss'),
                conversation_id: conversationId || '',
                previous_messages: messages.slice(0, index + 1).map((pm, pmIndex, arr) => ({
                    is_user: pm.sender === SENDER.user,
                    message: pm.text,
                    is_flagged: pmIndex === arr.length - 1,
                })),
                user_comments: '',
                is_impersonating:
                    userInfo?.originalLoginId !== undefined && userInfo.originalLoginId !== userInfo.externalId,
                original_user_id: userInfo?.originalLoginId,
                organization:
                    userInfo?.organizations &&
                    userInfo?.currentOrganization &&
                    userInfo.organizations.some((org) => org.id === userInfo.currentOrganization)
                        ? userInfo.organizations.find((org) => org.id === userInfo.currentOrganization)?.name || ''
                        : '',
                typology: userInfo?.typology || '',
                environment: isProduction() ? 'Producción' : 'Pruebas',
                agent_version: agentVersion,
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [messages, conversationId, agentVersion, userInfo],
    );

    return (
        <section
            ref={chatContainerRef}
            className={`flex relative flex-col space-y-12 px-6 pt-5 ${
                messages.length > 0 ? 'pb-[80vh]' : ''
            } w-full h-full max-w-[860px] self-center [--bg-blend-mode:multiply] dark:[--bg-blend-mode:luminosity]`}
        >
            {customBackgroundImage && (
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage: `url(${customBackgroundImage})`,
                            backgroundSize: 'contain',
                            backgroundPosition: 'center center',
                            backgroundRepeat: 'no-repeat',
                            opacity: 0.075,
                            filter: 'contrast(1.1)',
                            mixBlendMode: 'var(--bg-blend-mode, multiply)' as React.CSSProperties['mixBlendMode'],
                        }}
                    />

                    <div
                        className="absolute inset-0"
                        style={{
                            background: `radial-gradient(ellipse 60% 50% at center, transparent 20%, rgba(var(--background), 0.3) 50%, rgba(var(--background), 0.8) 80%, rgba(var(--background), 1) 100%)`,
                            mixBlendMode: 'normal',
                        }}
                    />

                    <div
                        className="absolute inset-0"
                        style={{
                            background: `linear-gradient(to right, rgba(var(--background), 0.9) 0%, transparent 15%, transparent 85%, rgba(var(--background), 0.9) 100%),
                                        linear-gradient(to bottom, rgba(var(--background), 0.9) 0%, transparent 15%, transparent 85%, rgba(var(--background), 0.9) 100%)`,
                        }}
                    />
                </div>
            )}

            <div className="flex flex-col flex-grow relative">
                <motion.div
                    className="flex flex-col space-y-12 flex-grow"
                    animate={{ justifyContent: isKnowledge && messages.length === 0 ? 'center' : 'flex-start' }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                    {messages.length === 0 && !isKnowledgeLoading && (
                        <AnimatePresence>
                            <motion.div
                                className="flex flex-col space-y-2"
                                initial={{ opacity: 0, y: -50 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.7, ease: 'easeOut', exit: { duration: 0 } }}
                            >
                                <MobileAppBanner />
                                <motion.div
                                    className="flex flex-col space-y-2"
                                    animate={{ y: isActive && !isKnowledge ? 200 : 0 }}
                                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <h1
                                        className="text-3xl font-poppins font-semibold w-full"
                                        data-testid="welcome-message"
                                    >
                                        {isKnowledge
                                            ? t('chat.greatingsKnowledge', {
                                                  name: userInfo?.firstName,
                                                  orgName:
                                                      userInfo?.organizations?.find(
                                                          (org) => org.id === userInfo?.currentOrganization,
                                                      )?.name ?? '',
                                              })
                                            : t('chat.greatings', { name: userInfo?.firstName })}
                                    </h1>
                                    <h1
                                        className="text-3xl font-poppins font-semibold w-full text-transparent bg-clip-text bg-gradient-to-r from-[#B998FA] to-[#3788F8]"
                                        data-testid="ask-message"
                                    >
                                        {t('chat.ask')}
                                    </h1>
                                </motion.div>
                            </motion.div>
                        </AnimatePresence>
                    )}
                    <AnimatePresence>
                        {messages.length === 0 && !chatInputValue && !files.length && mode !== CHAT_MODE.knowledge && (
                            <motion.div
                                className=""
                                initial={{ opacity: 0, y: 50 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], exit: { duration: 0 } }}
                            >
                                <Cards
                                    items={suggestedQuestions}
                                    quantityQuestions={suggestedQuestionsQuantity}
                                    setChatInputValue={setChatInputValue}
                                    sendMessage={handleSendLogisticsCard}
                                    defaultQuestions={defaultSuggestedQuestionTitles}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <div ref={ref} data-testid="chat-messages">
                        <AnimatePresence>
                            {messages.length > 0 && (
                                <motion.div
                                    className="space-y-6"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    ref={messagesListRef}
                                >
                                    {messages.map((msg, index) => {
                                        const stableId = msg.localId || msg.interactionId;
                                        const uniqueKey = [stableId, msg.sender, index].filter(Boolean).join('-');
                                        return (
                                            <motion.div
                                                id={`message-${uniqueKey}`}
                                                key={uniqueKey}
                                                data-sender={msg.sender === SENDER.user ? 'user' : 'agent'}
                                            >
                                                {msg.sender === SENDER.user ? (
                                                    <UserMessage
                                                        text={msg.text}
                                                        files={msg.files || []}
                                                        interactionId={msg.interactionId}
                                                        chatMode={mode}
                                                    />
                                                ) : (
                                                    <Response
                                                        key={uniqueKey}
                                                        id={`${msg.interactionId || msg.localId}`}
                                                        interactionId={msg.interactionId}
                                                        ratingMsg={msg.rating}
                                                        text={msg.text}
                                                        animated={msg.animated}
                                                        attachment={msg?.attachments}
                                                        mailToken={firebaseIdToken}
                                                        mail={userInfo?.email}
                                                        messageType={msg.messageType}
                                                        payload={messagePayloads[index]}
                                                    />
                                                )}
                                            </motion.div>
                                        );
                                    })}
                                    {isLoading &&
                                        messages.length > 0 &&
                                        messages[messages.length - 1]?.sender === SENDER.user && (
                                            <motion.div
                                                className="px-2 self-start"
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -20 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                {mode === CHAT_MODE.logistics ? (
                                                    <div className="flex items-center gap-3 py-2">
                                                        <ThinkingLoader />
                                                        <span className="animate-pulse italic">
                                                            {progressiveThinkingMessage.endsWith('...') ? (
                                                                <>
                                                                    {progressiveThinkingMessage.slice(0, -3)}
                                                                    <AnimatedDots />
                                                                </>
                                                            ) : (
                                                                progressiveThinkingMessage
                                                            )}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2 py-2">
                                                        <ThinkingLoader />
                                                    </div>
                                                )}
                                            </motion.div>
                                        )}
                                    <div ref={bottomRef} className="h-px w-full" />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {showRestScrollButton && buttonTop !== null && (
                        <motion.button
                            initial={{ top: buttonTop - 60 }}
                            animate={{ top: buttonTop }}
                            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                            style={{
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                zIndex: 50,
                                borderRadius: '50%',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                width: 48,
                                height: 48,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: 'none',
                            }}
                            className="bg-white dark:bg-gray-700"
                            onClick={() => {
                                scrollToBottomAndUnlock('smooth');
                            }}
                            aria-label="Ir al final"
                        >
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M12 5v14m0 0l-7-7m7 7l7-7"
                                    stroke="#2563eb"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="dark:stroke-white"
                                />
                            </svg>
                        </motion.button>
                    )}
                </motion.div>
            </div>

            <div
                ref={inputContainerRef}
                className="transition-transform animate-in fade-in duration-700 ease-in fixed bottom-0 px-6"
            >
                {/* <div className="bg-gradient-to-b from-background/0 to-background h-12"></div> */}
                <div className="bg-background">
                    <Input
                        data-testid="chat-input"
                        ref={inputRef}
                        chatInputValue={chatInputValue}
                        setChatInputValue={setChatInputValue}
                        files={files}
                        setFiles={setFiles}
                        onSendMessage={() => handleSendMessage({ mode, source: SOURCE.WEB })}
                        onCancel={handleCancelStream}
                        canCancel={isStreaming || Boolean(activeKnowledgeRequestId)}
                        isLoading={isLoading}
                        isStreaming={isStreaming}
                        isDifferentAgentVersion={isDifferentAgentVersion}
                        isModeLocked={isModeLocked}
                        showKnowledgeEmbeddingSelector={showKnowledgeEmbeddingSelector}
                        knowledgeEmbeddingDestination={knowledgeEmbeddingDestination}
                        setKnowledgeEmbeddingDestination={setKnowledgeEmbeddingDestination}
                    />
                </div>

                <div data-test-id="disclaimer" className="h-12 bg-background flex flex-col align-middle justify-center">
                    {showDisclaimer && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7 }}>
                            <p className="text-xs text-gray-400 text-center my-2">{t('chat.disclaimer')}</p>
                        </motion.div>
                    )}
                </div>
            </div>
            <p className="fixed right-2 bottom-2 text-xs text-gray-500">{MINDY_VERSION}</p>
        </section>
    );
}

export default Chat;
